import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { Download, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const Reports = () => {
  const [rankings, setRankings] = useState([]);
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadRankings();
  }, [month, year]);

  const loadRankings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/reports/ranking?month=${month}&year=${year}`);
      setRankings(response.data);
    } catch (error) {
      toast.error('Failed to load rankings');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API}/reports/export-pdf?month=${month}&year=${year}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ranking_${month}_${year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/reports/export-csv?month=${month}&year=${year}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ranking_${month}_${year}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const chartData = rankings.slice(0, 10).map(r => ({
    name: r.student_name.split(' ')[0],
    score: r.score,
    stars: r.total_stars
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-section">
      <Card className="p-6 bg-white shadow-lg border-0">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Monthly Ranking Reports</h3>
          <div className="flex items-center space-x-3">
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-32" data-testid="month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>
                    {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-32" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 5 }, (_, i) => (
                  <SelectItem key={i} value={String(new Date().getFullYear() - i)}>
                    {new Date().getFullYear() - i}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleExportPDF} variant="outline" className="flex items-center space-x-2" data-testid="export-pdf-button">
              <Download className="w-4 h-4" />
              <span>PDF</span>
            </Button>
            <Button onClick={handleExportCSV} variant="outline" className="flex items-center space-x-2" data-testid="export-csv-button">
              <Download className="w-4 h-4" />
              <span>CSV</span>
            </Button>
          </div>
        </div>

        {/* Chart */}
        <div className="mb-8">
          <h4 className="text-md font-semibold mb-4 text-gray-700">Top 10 Students</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="score" fill="#3b82f6" name="Total Score" />
              <Bar dataKey="stars" fill="#f59e0b" name="Stars" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Rankings Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Rank</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Student Name</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Stars</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Attendance</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Tasks</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rankings.map((rank, idx) => (
                <tr
                  key={rank.student_id}
                  className={idx < 3 ? 'bg-yellow-50' : 'hover:bg-gray-50'}
                  data-testid={`rank-row-${idx + 1}`}
                >
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                      idx === 0 ? 'bg-yellow-400 text-white' :
                      idx === 1 ? 'bg-gray-400 text-white' :
                      idx === 2 ? 'bg-orange-400 text-white' :
                      'bg-gray-200 text-gray-700'
                    }`}>
                      {rank.rank}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">{rank.student_name}</td>
                  <td className="px-4 py-3 text-center">{rank.total_stars}</td>
                  <td className="px-4 py-3 text-center">{rank.attendance_count}</td>
                  <td className="px-4 py-3 text-center">{rank.completed_tasks}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 font-semibold rounded-full text-sm">
                      {rank.score}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && <p className="text-center text-gray-500 py-8">Loading...</p>}
        {!loading && rankings.length === 0 && <p className="text-center text-gray-500 py-8">No data available</p>}
      </Card>
    </div>
  );
};

export default Reports;